import { useCallback, useEffect, useMemo, useState } from "react"

import { finalize } from "rxjs/operators"

import { GetNotificationMethodsObservable } from "./NotificationContactsProcess"
import handleAPIError from "../../../utils/handleAPIError"
import NotificationMethod from "../../Notifications/Model/NotificationMethod"

/**
 * Custom hook to manage notification methods.
 *
 * @param {NotificationMethod[]} selectedMethods - The initially selected notification methods.
 * @returns {[boolean, Record<string, NotificationMethod>, React.Dispatch<React.SetStateAction<Record<string, NotificationMethod>>>, () => void]}
 *   An array containing:
 *   - loading: A boolean indicating if the data is being loaded.
 *   - methods: A record of notification methods.
 *   - setMethods: A function to update the methods state.
 *   - resetMethodsSelection: A function to reset the selection state of methods.
 */
function useNotificationMethods(
  selectedMethods: NotificationMethod[] = []
): [
  loading: boolean,
  methods: Record<string, NotificationMethod>,
  setMethods: React.Dispatch<React.SetStateAction<Record<string, NotificationMethod>>>,
  resetMethodsSelection: () => void,
] {
  const [loading, setLoading] = useState(false)
  const [methods, setMethods] = useState<Record<string, NotificationMethod>>({})

  useEffect(() => {
    setLoading(true)
    const subscription = GetNotificationMethodsObservable()
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (data) => {
          const methodMap = data.reduce<Record<string, NotificationMethod>>((acc, method) => {
            acc[method.Code] = {
              ...method,
              selected: selectedMethods.some(
                (selectedMethod) => selectedMethod.NotificationMethodID === method.NotificationMethodID
              ),
            }
            return acc
          }, {})
          setMethods(methodMap)
        },
        error: handleAPIError,
      })

    return () => subscription.unsubscribe()
  }, [selectedMethods])

  /**
   * Reset the selection state of all methods to false.
   */
  const resetMethodsSelection = useCallback(() => {
    setMethods((prev) => {
      const updatedMethods = Object.entries(prev).reduce<Record<string, NotificationMethod>>((acc, [key, method]) => {
        acc[key] = { ...method, selected: false }
        return acc
      }, {})
      return updatedMethods
    })
  }, [])

  return useMemo(
    () => [loading, methods, setMethods, resetMethodsSelection],
    [loading, methods, setMethods, resetMethodsSelection]
  )
}

export default useNotificationMethods
